import { useMemo } from 'react';
import { FilterMenu, type FilterOption } from '@/components/shared/filter-menu';
import type { WorkflowCategoryTree } from '@/services/workflow.service';

export interface CategoryFilter {
  category?: string;
  subcategory?: string;
  uncategorized?: boolean;
}

/**
 * Category filter over every protocol, not just the loaded page: the busiest categories as pills,
 * and every category, subcategory and the uncategorized ones in the dropdown.
 */
export function ProtocolCategoryTabs({
  tree,
  active,
  onSelect
}: {
  tree: WorkflowCategoryTree;
  active: CategoryFilter;
  onSelect: (filter: CategoryFilter) => void;
}) {
  const options = useMemo<FilterOption[]>(
    () => [
      ...tree.categories.map((c) => ({
        label: c.category,
        value: encode({ category: c.category }),
        count: c.count,
        children: c.subcategories.map((sub) => ({
          label: sub.name,
          value: encode({ category: c.category, subcategory: sub.name }),
          count: sub.count,
          // Pills are for categories; subcategories are picked from the dropdown.
          weight: -1
        }))
      })),
      ...(tree.uncategorized > 0
        ? [{ label: 'Uncategorized', value: encode({ uncategorized: true }), count: tree.uncategorized }]
        : [])
    ],
    [tree]
  );
  const total = tree.categories.reduce((sum, c) => sum + c.count, 0) + tree.uncategorized;

  if (total === 0) return null;

  return (
    <FilterMenu
      label='Categories'
      options={options}
      value={active.category || active.uncategorized ? encode(active) : undefined}
      onChange={(value) => onSelect(value ? (JSON.parse(value) as CategoryFilter) : {})}
      total={total}
    />
  );
}

/** A filter as an option value; key order is fixed so the same filter always encodes the same. */
const encode = ({ category, subcategory, uncategorized }: CategoryFilter) =>
  JSON.stringify(uncategorized ? { uncategorized: true } : { category, ...(subcategory ? { subcategory } : {}) });
